import React, { useEffect } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'
import clsx from 'clsx'

import { OrgPermission } from '@makyoapp/makyo-sdk/models/ACL'
import { Loading } from '@makyoapp/makyo-ui'

import { CurrentOrgProvider, useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useOrgsContext } from '@/context/OrgsContext'
import { useOrgPerm } from '@/hooks/useOrgPerm'
import { useParamId } from '@/hooks/useParamId'
import {
  ORG_ACL_ROUTE,
  ORG_APP_ROUTE,
  ORG_BILLING_ROUTE,
  ORG_BRAND_KIT_ROUTE,
  ORG_DATABASE_ROUTE,
  ORG_ID_PARAM,
  ORG_MEMBER_ROUTE,
  ORG_STORAGE_ROUTE,
  ORG_TEAM_ROUTE,
  PROJECT_PLUGIN_ROUTE,
} from '@/shared/constants'
import { RoutePropsWithComponent } from '@/shared/routes'
import Layout from '@/components/Layout'
import SidebarMenu, { Sidebar } from '@/components/Sidebar'

import { UserTrashMenuItem } from '../user/UserTrashMenuItem'
import OrgACLRoutes from './access-control/acl.routes'
import OrgDetailAppRoutes from './apps'
import OrgDetailBillingRoutes from './billing/org-billing.routes'
import OrgBrandKitRoutes from './brand-kit/brand-kit.routes'
import OrgDashboardPage from './dashboard'
import OrgDatabaseRoutes from './databases/databases.routes'
import OrgDetailMemberPage from './members'
import OrgPluginsRoutes from './plugins/plugins.routes'
import { orgDetailNavLink } from './shared'
import OrgDetailStorage from './storage'
import OrgTeamsRoutes from './teams/teams.routes'

const orgDetailRoutes = [
  {
    path: `${ORG_ID_PARAM}`,
    index: true,
    name: 'Welcome',
    component: OrgDashboardPage,
    icon: 'Building16',
    order: 1,
  },
  {
    path: `${ORG_ID_PARAM}/${ORG_APP_ROUTE}/*`,
    name: 'Apps',
    component: OrgDetailAppRoutes,
    icon: 'App16',
    order: 1,
  },
  {
    path: `${ORG_ID_PARAM}/websites`,
    name: 'Websites',
    component: () => {
      return <Layout title='Websites'>websites</Layout>
    },
    icon: 'Browser16',
    order: 1,
  },
  {
    path: `${ORG_ID_PARAM}/${ORG_BRAND_KIT_ROUTE}/*`,
    name: 'Brand Kits',
    component: OrgBrandKitRoutes,
    icon: 'Brush16',
    order: 1,
  },
  {
    path: `${ORG_ID_PARAM}/${ORG_DATABASE_ROUTE}/*`,
    name: 'Databases',
    component: OrgDatabaseRoutes,
    icon: 'Db16',
    order: 2,
  },
  {
    path: `${ORG_ID_PARAM}/${ORG_BILLING_ROUTE}/*`,
    name: 'Billing & Subscription',
    component: OrgDetailBillingRoutes,
    icon: 'Receipt16',
    order: 3,
  },
  {
    path: `${ORG_ID_PARAM}/${ORG_MEMBER_ROUTE}/*`,
    name: 'Members',
    component: OrgDetailMemberPage,
    icon: 'Customer16',
    order: 4,
  },
  {
    path: `${ORG_ID_PARAM}/${ORG_TEAM_ROUTE}/*`,
    name: 'Teams',
    component: OrgTeamsRoutes,
    icon: 'Team16',
    order: 4,
  },
  {
    path: `${ORG_ID_PARAM}/${ORG_ACL_ROUTE}/*`,
    name: 'ACL',
    component: OrgACLRoutes,
    icon: 'Shield16',
    order: 5,
  },
  {
    path: `${ORG_ID_PARAM}/${ORG_STORAGE_ROUTE}/*`,
    name: 'Storage',
    component: OrgDetailStorage,
    icon: 'Storage16',
    order: 5,
  },
  {
    path: `${ORG_ID_PARAM}/${PROJECT_PLUGIN_ROUTE}/*`,
    name: 'Plugins',
    component: OrgPluginsRoutes,
    icon: 'Plug16',
    order: 6,
  },
]

function Wrapper({
  children,
  menu,
  isLoading,
}: {
  menu?: React.ReactNode
  children: React.ReactNode
  isLoading: boolean
}) {
  return isLoading ? (
    <Loading />
  ) : menu ? (
    <div className='wrapper'>
      {menu}
      <div className={clsx('flex-1 overflow-auto mk-scroll [scrollbar-gutter:stable] h-full')}>{children}</div>
    </div>
  ) : (
    <>{children}</>
  )
}

function OrgDetailRoutes() {
  const { currentOrg, isLoading } = useCurrentOrgContext()
  const { orgsPerms, updateOrgsPerms } = useOrgsContext()
  const location = useLocation()
  const orgId = useParamId('org')
  const canAccessBrandKit = useOrgPerm(OrgPermission.BrandKitAccess)

  useEffect(() => {
    if (currentOrg) {
      if (!orgsPerms[currentOrg.id]) {
        updateOrgsPerms([currentOrg])
      }
    }
  }, [currentOrg, orgsPerms, updateOrgsPerms])

  const filteredRoutes = orgDetailRoutes.filter((route) => {
    // If it's the brand kit route and user doesn't have access, filter it out
    if (route.path?.includes(ORG_BRAND_KIT_ROUTE) && !canAccessBrandKit) {
      return false
    }
    return true
  })

  const menu = (
    <SidebarMenu
      menu={
        filteredRoutes.sort((a, b) => {
          const orderA = a.order || 0
          const orderB = b.order || 0
          return orderA - orderB
        }) as RoutePropsWithComponent[]
      }
      //org={currentOrg}
      getTo={(path) => {
        return orgDetailNavLink(path, location, orgId)
      }}
    >
      <UserTrashMenuItem />
    </SidebarMenu>
  )

  const split = location.pathname.split('/')
  const isDbRoute = split.length > 4 && split?.[3] === ORG_DATABASE_ROUTE
  const sidebarMenu = <Sidebar menu={menu} />

  return (
    <Wrapper menu={!isDbRoute ? sidebarMenu : undefined} isLoading={isLoading}>
      {currentOrg && !isLoading && (
        <React.Suspense fallback={<Loading fullHeight />}>
          <Routes>
            {filteredRoutes.map(({ name, component: Comp, ...r }) => {
              return (
                <Route
                  key={`org-detail-${name}`}
                  path={r.index ? undefined : r.path.replace(`${ORG_ID_PARAM}/`, '')}
                  index={r.index}
                  element={<Comp title={name} />}
                />
              )
            })}
          </Routes>
        </React.Suspense>
      )}
    </Wrapper>
  )
}

export default function OrgRoutes() {
  return (
    <CurrentOrgProvider>
      <OrgDetailRoutes />
    </CurrentOrgProvider>
  )
}
